/**
 * media_kind derivation + copy tables for the in-chat Thinking timeline.
 *
 * Ported from the backend `media_kind` classifier so the frontend can key
 * each timeline step's phrase + icon off the returned content, without a
 * backend round-trip. Pure — no React.
 */

import type { RAGReference } from '@/types/rag';

export type MediaKind =
  | 'meditation'
  | 'quote'
  | 'short'
  | 'article'
  | 'talk'
  | 'video'
  | 'other';

type RefLike = Pick<RAGReference, 'type' | 'category' | 'url' | 'title'>;

export function deriveMediaKind({ type, category, url }: RefLike): MediaKind {
  const cat = (category ?? '').toLowerCase();
  const u = url ?? '';
  if (u.includes('/shorts/') || u.includes('#shorts')) return 'short';
  if (cat.includes('quote')) return 'quote';
  if (type === 'Text') return 'article';
  if (type === 'Video') {
    if (cat.includes('meditation')) return 'meditation';
    if (
      cat.includes('talk') ||
      cat.includes('satsang') ||
      cat.includes('wisdom') ||
      cat.includes('knowledge')
    ) {
      return 'talk';
    }
    return 'video';
  }
  return 'other';
}

/** The step-2 phrase shown next to the media icon, keyed by media_kind. */
export const MEDIA_KIND_PHRASE: Record<MediaKind, string> = {
  meditation: 'Pulled a real guided meditation to share',
  quote: "Found a line of Gurudev's wisdom",
  short: 'Found a short clip to watch',
  article: 'Found an article to read',
  talk: 'Found a talk by Gurudev',
  video: 'Found a video to watch',
  other: 'Found something to share',
};

/** Short fragment used in the collapsed one-line summary. */
export const MEDIA_KIND_SUMMARY: Record<MediaKind, string> = {
  meditation: 'found a guided meditation',
  quote: "found a line of Gurudev's wisdom",
  short: 'found a short clip',
  article: 'found an article',
  talk: 'found a talk',
  video: 'found a video',
  other: 'found something to share',
};

export type StepGlyph = 'youtube' | 'sparkle' | 'article';

export function mediaKindGlyph(kind: MediaKind): StepGlyph {
  switch (kind) {
    case 'quote':
      return 'sparkle';
    case 'article':
      return 'article';
    default:
      return 'youtube';
  }
}

const GENERIC_CATEGORIES = new Set([
  'general',
  'misc',
  'other',
  'content',
  'uncategorized',
  '',
]);

/**
 * Pick 1–2 distinctive human words from the refs' categories to fill the
 * "Consulted Art of Living guidance on {topic}" line. Returns null when the
 * categories are all generic/absent — callers then omit the " on {topic}".
 */
export function deriveTopic(refs: RAGReference[]): string | null {
  const seen: string[] = [];
  for (const r of refs) {
    for (const piece of (r.category ?? '').split(/[,/|]/)) {
      const c = piece.trim().toLowerCase();
      if (c && !GENERIC_CATEGORIES.has(c) && !seen.includes(c)) seen.push(c);
    }
  }
  if (seen.length === 0) return null;
  const pick = seen.slice(0, 2);
  return pick.length === 2 ? `${pick[0]} & ${pick[1]}` : pick[0];
}
